// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: one message per chat, so every row in the Chats panel can show what
// was said last, the way the connected WhatsApp rows do. The message cache only
// holds chats you have opened, and a reconnect empties it, so previews live
// here instead: fetched in one batched call, then kept current from the
// gateway. A chat is refetched only when the server knows of a newer message
// than the one we hold, which covers a cold start, a reconnect and anything
// missed while the app was closed.

import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import {needsPreviewFetch, pickNewerMessage} from '@app/features/channel/utils/ChannelPreviewModel';
import {Message} from '@app/features/messaging/models/MessagingMessage';
import Messages from '@app/features/messaging/state/MessagingMessages';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as UserCommands from '@app/features/user/commands/UserCommands';
import type {Message as WireMessage} from '@konvo/schema/src/domains/message/MessageResponseSchemas';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('Channel Previews');
/** The preload endpoint takes at most this many chats per call. */
const MAX_PREVIEW_BATCH = 100;
/** Long enough for a list render to settle into one request, short enough to be unnoticed. */
const PREVIEW_BATCH_DELAY_MS = 50;
/** A dropped connection is retried a few times before the rows wait for the next visit. */
const MAX_PREVIEW_RETRIES = 3;
const PREVIEW_RETRY_DELAY_MS = 2_000;

class ChannelPreviews {
	private previews: Record<string, Message> = {};
	private requested = new Map<string, string>();
	private pending = new Set<string>();
	private flushHandle: ReturnType<typeof setTimeout> | null = null;
	private failureStreak = 0;

	constructor() {
		makeAutoObservable<this, 'requested' | 'pending' | 'flushHandle' | 'failureStreak'>(
			this,
			{requested: false, pending: false, flushHandle: false, failureStreak: false},
			{autoBind: true},
		);
	}

	/** The newest message held for a chat, whether it came from the preview fetch or the gateway. */
	get(channelId: string): Message | null {
		return this.previews[channelId] ?? null;
	}

	/** Queue a fetch for every listed chat whose newest message we do not have yet. */
	ensureForChannels(channels: ReadonlyArray<Channel>): void {
		for (const channel of channels) {
			if (
				!needsPreviewFetch({
					lastMessageId: channel.lastMessageId,
					knownMessageId: this.knownMessageId(channel.id),
					requestedMessageId: this.requested.get(channel.id) ?? null,
				})
			) {
				continue;
			}
			this.pending.add(channel.id);
		}
		this.failureStreak = 0;
		this.scheduleFlush();
	}

	handleMessageCreate(message: WireMessage): void {
		if (!this.isChatChannel(message.channel_id)) return;
		this.store(message.channel_id, message);
	}

	handleMessageUpdate(message: WireMessage): void {
		const existing = this.previews[message.channel_id];
		if (!existing || existing.id !== message.id) return;
		// Edits can arrive as a partial payload; only a full one can rebuild the line.
		if (!message.author || message.content == null) return;
		this.previews[message.channel_id] = new Message(message);
	}

	handleMessageDelete(channelId: string, messageIds: ReadonlyArray<string>): void {
		const existing = this.previews[channelId];
		if (!existing || !messageIds.includes(existing.id)) return;
		delete this.previews[channelId];
		// Let the next render ask again; the server answers with the message before it.
		this.requested.delete(channelId);
	}

	private store(channelId: string, wire: WireMessage): void {
		if (!wire.id || !wire.author) return;
		const message = new Message(wire);
		const newest = pickNewerMessage(this.previews[channelId], message);
		if (newest !== message) return;
		this.previews[channelId] = message;
	}

	private knownMessageId(channelId: string): string | null {
		const cached = Messages.getCachedMessages(channelId)?.last() ?? null;
		return pickNewerMessage(cached, this.previews[channelId])?.id ?? null;
	}

	private isChatChannel(channelId: string): boolean {
		const channel = Channels.getChannel(channelId);
		return channel?.isPrivate() === true || channel?.isText?.() === true;
	}

	private scheduleFlush(delayMs = PREVIEW_BATCH_DELAY_MS): void {
		if (this.flushHandle != null || this.pending.size === 0) return;
		this.flushHandle = setTimeout(() => {
			this.flushHandle = null;
			void this.flush();
		}, delayMs);
	}

	private async flush(): Promise<void> {
		const channelIds = Array.from(this.pending).slice(0, MAX_PREVIEW_BATCH);
		if (channelIds.length === 0) return;
		for (const channelId of channelIds) {
			this.pending.delete(channelId);
			const lastMessageId = Channels.getChannel(channelId)?.lastMessageId;
			if (lastMessageId) {
				this.requested.set(channelId, lastMessageId);
			}
		}
		try {
			const fetched = await UserCommands.fetchLatestChannelMessages(channelIds);
			runInAction(() => {
				for (const [channelId, wire] of Object.entries(fetched)) {
					if (!wire) continue;
					this.store(channelId, wire);
				}
			});
			this.failureStreak = 0;
			this.scheduleFlush();
		} catch (error) {
			// A dropped request is worth a few retries; after that the rows fill in
			// the next time the panel asks, rather than hammering a dead connection.
			logger.debug('Failed to fetch chat previews', error);
			this.failureStreak += 1;
			for (const channelId of channelIds) {
				this.requested.delete(channelId);
				if (this.failureStreak <= MAX_PREVIEW_RETRIES) {
					this.pending.add(channelId);
				}
			}
			this.scheduleFlush(PREVIEW_RETRY_DELAY_MS * this.failureStreak);
		}
	}
}

export default new ChannelPreviews();
